import { useCheckboxContext } from "@/features/workByManagement/context/CheckboxProvider";
import { usePub, useSub } from "@/hooks";
import { theme } from "@/styles/theme";
import styled from "@emotion/styled";
import { Checkbox, Flex, Table, Text, TextInput } from "@mantine/core";
import {
  AuthSignupPost201ResponseEquipmentWorksInner,
  ProductionPlansGet200ResponseRowsInnerWorksInner,
  WorksItemGet200ResponseRowsInner,
  WorksSlittingScrapDetailPost200ResponseInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useWorkActions } from "../workByManagement/hooks/useWorkActions";
import { useWorkState } from "../workByManagement/hooks/useWorkState";
import { Td } from "../workByManagement/WorkTableRow";
import { WorkSlittingProgressCell } from "./workSlittingProgressCell";

export interface rowDataProps extends WorksItemGet200ResponseRowsInner {
  slittingData: {
    workId: number;
    scrapWeight: string;
    widthWithScrap: string;
    widthWithoutScrap: string;
    usedMaterialWeight: string;
  };
}
interface WorkOperationTableRowInRowProps {
  rowData: WorksItemGet200ResponseRowsInner;
  rowIndex: number;
  formReset: boolean;
  slittingData: WorksSlittingScrapDetailPost200ResponseInner | undefined;
  partChecked?: Record<number, boolean>;
  workIds?: number[];
}
const workStatusColors = {
  WAITING: "",
  WORKING: theme.colors?.green?.[0],
  PAUSED: theme.colors?.orange?.[0],
  DONE: theme.colors?.gray?.[4],
};
const defaultWorkStatusColor = theme.colors?.gray?.[4];
export const WorkSlittingTableInRowBody = (params: WorkOperationTableRowInRowProps) => {
  const { rowData, rowIndex, formReset, slittingData, partChecked, workIds } = params;
  const state = useWorkState();
  const actions = useWorkActions();
  const { areAllChecked } = useCheckboxContext();
  const checkRef = useRef<HTMLInputElement>(null);
  const [checkedValues, setCheckedValues] = useState(false);
  const publish = usePub();
  const { t } = useTranslation();

  useSub("checkAll", () => {
    setCheckedValues(true);
  });

  useSub("unCheckAll", () => {
    setCheckedValues(false);
  });

  useEffect(() => {
    areAllChecked();
  }, [areAllChecked()]);

  useEffect(() => {
    if (formReset) {
      setCheckedValues(false);
    }
  }, [formReset]);

  useEffect(() => {
    const id = rowData?.id;
    if (!id) return;

    const isChecked = Object.keys(state.scrapWorkIds ?? {})
      .map(Number)
      .includes(id);

    const exists = state.works.some((work) => work?.id === id);

    if (isChecked && !exists) {
      actions.setWorks((prev) => [...prev, rowData]);
    }

    if (!isChecked && exists) {
      setCheckedValues(false);
      actions.setWorks((prev) => prev.filter((work) => work?.id !== id));
    }
  }, [state.scrapWorkIds, rowData?.id]);

  const shouldRenderTwice =
    workIds &&
    Object.keys(workIds).some((key) => {
      const numKey = Number(key);
      return partChecked?.[numKey] === true;
    });

  const splitEvenly = (value: number): [number, number] => {
    const lower = Math.floor(value / 2);
    const upper = value - lower;
    return [lower, upper];
  };

  // tbody 한 블록으로 추출
  const renderTbodyRow = (scrap: number, used: number, length: number) => (
    <tbody key={`${scrap}-${used}-${length}`}>
      <WorkRowTr
        color={
          rowData.id && rowData.trackingStatus
            ? workStatusColors[rowData.trackingStatus]
            : defaultWorkStatusColor
        }
      >
        <Td minWidth={4} width={4}>
          <Flex align="center" justify="center">
            <Checkbox
              ref={checkRef}
              checked={
                Object.keys(state.scrapWorkIds ?? {})
                  .map(Number)
                  .includes(rowData?.id ?? 0) || checkedValues
              }
              onChange={(e) => {
                if (e.target.checked) {
                  publish("check");
                  setCheckedValues(true);
                  actions.setWorks(
                    (
                      prevWorks: (
                        | AuthSignupPost201ResponseEquipmentWorksInner
                        | ProductionPlansGet200ResponseRowsInnerWorksInner
                      )[],
                    ) => [...prevWorks, rowData],
                  );
                } else {
                  publish("unCheck");
                  setCheckedValues(false);
                  actions.setWorks(
                    (
                      prevWorks: (
                        | AuthSignupPost201ResponseEquipmentWorksInner
                        | ProductionPlansGet200ResponseRowsInnerWorksInner
                      )[],
                    ) => prevWorks.filter((work) => work?.id !== rowData?.id),
                  );
                }
              }}
            />
          </Flex>
        </Td>
        <Td minWidth={8} width={8}>
          {rowData.id}
        </Td>
        <Td minWidth={8} width={24}>
          <WorkSlittingProgressCell
            data={{
              itemName: (rowData as ProductionPlansGet200ResponseRowsInnerWorksInner)?.item
                ?.name as string,
              itemCode: (rowData as ProductionPlansGet200ResponseRowsInnerWorksInner)?.item
                ?.code as string,
              itemId: (rowData as ProductionPlansGet200ResponseRowsInnerWorksInner)?.item
                ?.id as number,
              itemSpec: (rowData as ProductionPlansGet200ResponseRowsInnerWorksInner)?.item
                ?.spec as string,
              workId: rowData.id,
              routingCode: rowData.routingCode,
              trackingStatus: (rowData as ProductionPlansGet200ResponseRowsInnerWorksInner)
                ?.trackingStatus,
              purchaseOrderItem: (rowData as ProductionPlansGet200ResponseRowsInnerWorksInner)
                ?.purchaseOrderItem,
            }}
          />
        </Td>
        <Td minWidth={8} width={16} style={{ textAlign: "right" }}>
          {slittingData?.scrapWeight ? (
            <Text>
              {scrap + used} / {used} / {scrap}
            </Text>
          ) : (
            <Text>{t("workMessage.scrapNone")}</Text>
          )}
        </Td>
        <Td minWidth={8} width={14}>
          <TextInput value={1} readOnly styles={{ input: { textAlign: "right" } }} />
        </Td>
      </WorkRowTr>
    </tbody>
  );

  return (
    <WorkRowTr>
      <Td colSpan={9}>
        <Table withColumnBorders withBorder>
          <thead>
            <WorkRowTr>
              <th></th>
              <th>{t("workLog.workId")}</th>
              <th>{t("work.progress")}</th>
              <th>
                {t("work.widthWithScrap")} / {t("work.widthWithoutScrap")} / {t("work.scrapWeight")}
              </th>
              <th>{t("summary.todoQuantity")}</th>
            </WorkRowTr>
          </thead>
          {(() => {
            const scrap = Number(slittingData?.scrapWeight ?? 0);
            const used = Number(slittingData?.usedMaterialWeight ?? 0);

            if (shouldRenderTwice) {
              const [scrap1, scrap2] = splitEvenly(scrap);
              const [used1, used2] = splitEvenly(used);

              return (
                <>
                  {renderTbodyRow(scrap1, used1, splitEvenly(scrap).length - 1)}
                  {renderTbodyRow(scrap2, used2, splitEvenly(scrap).length)}
                </>
              );
            } else {
              return renderTbodyRow(scrap, used, splitEvenly(scrap).length);
            }
          })()}
        </Table>
      </Td>
    </WorkRowTr>
  );
};

// WorkRow 영역
const WorkRowTr = styled.tr`
  .mantine-1r8slib > thead > tr > th {
    background-color: ${theme.colors?.gray?.[0]};
    color: ${theme.colors?.gray?.[9]};
    font-weight: bold;
    text-align: center;
    padding: 0.5rem;
  }
  td {
    background-color: ${(props) => props.color};
  }
`;
